import { render, html } from "lit";
import { dom } from "@omegagrid/core";
import { msg } from "@omegagrid/localize";
import { CellModel, CellSource, ColumnSource, RowSource, renderers } from "@omegagrid/grid";
import rp, { AppComponent, GridEditorAdapter, GridEditorOptions, RowData, FileManager, FileManagerEvent } from "@reporting/rp-app-host";
import { FormContainer } from "@omegagrid/form";
import { AccountingGroup } from "@reporting/rp-types/core";
import { DomainCertificateFormAdapter } from "./domainCertificateFormAdapter";
import { PASSWORD_POLICIES } from "@reporting/rp-types/accounting";
import { url } from "../utils";

type DataSource = {
	/** The text displayed in the cell */
	text: CellSource | string;
	/** The value of the cell */
	value?: CellSource;
	/** The ID of the cell (hidden)*/
	id?: string;
	/** The type of the data, e.g., 'AccountingGroup' (hidden) */
	type?: string;
}

export class LoginSettingsGridEditorAdapter extends GridEditorAdapter {

	private _columns: ColumnSource[];
	private _rows: RowSource[];
	private group: AccountingGroup;

	async getColumns(): Promise<ColumnSource[]> {
		if (this._columns) return this._columns;
		this._columns = [{
			data: 'text',
			t: 't',
			e: false,
			levelButtons: true,
			levelIndent: true,
			w: 260,
		}, {
			data: 'value',
			t: 't',
			s: { alignment: { horizontal: 'left' } },
			w: 400,
		}, {
			data: 'id',
			t: 't',
			e: false,
			hidden: true,
		}, {
			data: 'type',
			t: 't',
			e: false,
			hidden: true,
		},];
		return this._columns;
	}

	async getRows(): Promise<RowSource[]> {
		this.group = await rp.ms('accounting').get<AccountingGroup>('groups/mine');
		const pwdComplexityList = Object.values(PASSWORD_POLICIES).map((p) => ({ key: p.id, value: msg(p.text) }));
		this._rows = [
			{ l: 0, data: { text: { cs: 2, v: msg('General') } } as DataSource },
				{ l: 1, data: {
					text: msg('Disable manual login'),
					value: { t: 'b', v: this.group.disable_manual_login },
					id: 'disable_manual_login',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Enable permanent login'),
					value: { t: 'b', v: this.group.enable_permanent_login === false ? false : true },
					id: 'enable_permanent_login',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Always use captcha verification'),
					value: { t: 'b', v: this.group.enable_captcha },
					id: 'enable_captcha',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Disable password change'),
					value: { t: 'b', v: this.group.disable_password_change },
					id: 'disable_password_change',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Password complexity'),
					value: { t: 's', v: this.group.password_complexity, dv: msg(PASSWORD_POLICIES[this.group.password_complexity]?.text), content: pwdComplexityList },
					id: 'password_complexity',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Custom domain'),
					value: { t: 't', v: this.group.custom_domain },
					id: 'custom_domain',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Domain certificate'),
					value: { t: 't', e: false, v: null, renderer: this.certRenderer },
					id: 'domain_certificate',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Max login attempts'),
					value: { t: 'n', v: this.group.login_max_attempts },
					id: 'login_max_attempts',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Login attempt timeout (sec)'),
					value: { t: 'n', v: this.group.login_attempts_timeout },
					id: 'login_attempts_timeout',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('IP address filter'),
					value: { t: 't', v: this.group.ip_filter },
					id: 'ip_filter',
					type: 'AccountingGroup',
				} as DataSource },

			{ l: 0, collapsed: !this.group.custom_login, data: { text: { cs: 2, v: msg('Customized login page') } } as DataSource },
				{ l: 1, data: {
					text: msg('Enabled'),
					value: { t: 'b', v: this.group.custom_login },
					id: 'custom_login',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Custom URL'),
					value: { t: 't', e: false, v: url.getCustomAuthUrl(this.group) },
					id: 'custom_auth_url',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Style'),
					value: { t: 'n', v: this.group.custom_login_style }, // TODO - add style manager???
					id: 'custom_login_style',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Logo'),
					value: { t: 't', e: false, v: this.group.custom_login_id_image, renderer: this.iconRenderer },
					id: 'custom_login_id_image',
					type: 'AccountingGroup',
				} as DataSource },
				{ l: 1, data: {
					text: msg('Favicon'),
					value: { t: 't', e: false, v: this.group.custom_id_favicon, renderer: this.iconRenderer },
					id: 'custom_id_favicon',
					type: 'AccountingGroup',
				} as DataSource },
		];
		return this._rows;
	}

	getOptions(): Partial<GridEditorOptions> {
		return this.createOptions({
			colLetters: false,
			rowNumbers: false,
			columnSorting: false,
			filters: false,
			refreshAfterSave: true,
			rowTree: false,
			saveMode: 'edited_rows',
			editable: rp.userProvider.isGroupAdmin(),
			toolbar: {
				new: { enabled: false },
				save: { enabled: rp.userProvider.isGroupAdmin() },
				discard: { enabled: rp.userProvider.isGroupAdmin() },
			},
		});
	}

	async save(rowData: RowData[]) {
		const data: AccountingGroup = {};
		const gridData = rowData.map(row => (row.data as DataSource));
		for (const d of gridData) {
			if (d.id && d.type === 'AccountingGroup') {
				(data as any)[d.id] = d.value;
			}
		}
		data.id_group = this.group.id_group;
		await rp.ms('accounting').post('groups', data);
	}

	async onAction(action: string, cell: CellModel) {
		switch (action) {
			case 'fileManagerBtn':
				const component = dom.createElement<FileManager>('rp-definition-filemanager', {
					client: rp.ms('definition'),
				});
				component.addEventListener('filemanager.select', (e: FileManagerEvent) => {
					this.editor.setCellValue(cell.row.index, cell.column.index, e.file.id_file, e.file.filename);
				});
				rp.host.openModalSidebar({
					component,
					caption: `${msg('File Manager')} - ${cell.row.data.text}`,
					width: 600,
				});
				break;
			case 'removeBtn':
				this.editor.setCellValue(cell.row.index, cell.column.index, null);
				break;
			case 'domainCertBtn': {
				const component = dom.createElement<FormContainer>('og-form-container');
				component.adapter = new DomainCertificateFormAdapter();
				rp.host.openModalSidebar({
					component: component as unknown as AppComponent,
					caption: `${msg('Domain certificate')} - ${this.group.custom_domain}`,
					width: 600,
				});
				break;
			}
		}
	}

	/** the certificate button of the domain certificate row - the domain must be saved first */
	certRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(html`
				<og-button
					icon="file-certificate"
					color="gray"
					.disabled="${!rp.userProvider.isGroupAdmin() || !this.group.custom_domain}"
					style="height: 18px; width: 18px"
					title="${msg('Domain certificate')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('domainCertBtn', cell)}">
				</og-button>
			`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

	iconRenderer = (div: HTMLDivElement, cell: CellModel) => {
		dom.empty(div);
		div = renderers.BaseRenderer(div, cell);

		const innerDiv = dom.createElement<HTMLDivElement>('div');
		innerDiv.style.display = 'flex';
		innerDiv.style.flexDirection = 'row';
		innerDiv.style.gap = '4px';
		innerDiv.style.alignItems = 'center';
		innerDiv.style.minHeight = '22px';

		render(html`
				<og-button
					icon="pen"
					color="gray"
					.disabled="${!rp.userProvider.isGroupAdmin()}"
					style="height: 18px; width: 18px"
					title="${msg('Edit')}"
					@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
					@click="${() => this.onAction('fileManagerBtn', cell)}">
				</og-button>
				${cell.value as number > 0 ? html`
					<og-button
						icon="xmark"
						color="red"
						.disabled="${!rp.userProvider.isGroupAdmin()}"
						style="height: 18px; width: 18px;"
						title="${msg('Remove')}"
						@pointerdown="${(e: PointerEvent) => e.stopPropagation()}"
						@click="${() => this.onAction('removeBtn', cell)}">
					</og-button>
					<div style="flex: 1;">
						<img src="/ms/${rp.accountingProvider.a}/definition/files/${cell.value}/download" style="height: 18px; position: relative; top: 2px" />
					</div>
				` : ''}
			`, innerDiv);

		div.prepend(innerDiv);
		return div;
	}

}
